/**
 * Call Status Utilities - Phase-Based Approach
 *
 * Core Phase Functions:
 * - isOfflinePhase()    - Agent offline/logged out
 * - isOnlinePhase()     - Agent available (online, preview, callback)
 * - isConnectingPhase() - Call ringing
 * - isConnectedPhase()  - Active call (includes mute/hold/transfer)
 * - isDisconnectedPhase() - Call ending
 * - isACWPhase()        - After call work
 * - isBreakPhase()      - Agent on break
 * - isInCallPhase()     - True if in any call phase
 * - isAvailableForCalls() - True if agent can take new calls
 */
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

// Status constants
export const ACW = 'ACW';
export const PREVIEW = 'preview';
export const PREVIEW_CAPITALIZE = 'Preview';
export const OFFLINE = 'offline';
export const CALLBACK = 'callback';
export const ONLINE = 'online';
export const BREAK = 'break';
export const LUNCH_BREAK = 'Lunch Break';
export const TEA_BREAK = 'Tea Break';
export const MEETING_BREAK = 'Meeting Break';
export const BIOMETRIC_BREAK = 'Biometric Break';
export const MOBILE_BREAK = 'Mobile Break';
export const LOGOUT = 'logout';
export const TRANSFER_START = 'TRANSFER_START';
export const TRANSFER_CALL = 'TRANSFER_CALL';
export const TRANSFER_TALK = 'TRANSFER_TALK';
export const AGENT_CONNECTING = 'AGENT_CONNECTING';
export const AGENT_RINING = 'AGENT_RINING';
export const AGENT_RINGING = 'AGENT_RINGING';
export const AGENT_CONNECTED = 'AGENT_CONNECTED';
export const CUSTOMER_CONNECTING = 'CUSTOMER_CONNECTING';
export const CUSTOMER_RINING = 'CUSTOMER_RINING';
export const CUSTOMER_RINGING = 'CUSTOMER_RINGING';
export const CUSTOMER_CONNECTED = 'CUSTOMER_CONNECTED';
export const CONNECTED = 'CONNECTED';
export const CUSTOMER_DISCONNECTED = 'CUSTOMER_DISCONNECTED';
export const CUSTOMER_DISCONNECTING = 'CUSTOMER_DISCONNECTING';
export const AGENT_MUTE = 'AGENT_MUTE';
export const MUTE = 'MUTE';
export const AGENT_HOLD = 'AGENT_HOLD';
export const HOLD = 'HOLD';

// Normalize status typos (e.g., RINING -> RINGING)
export const normalizeStatus = (status: string): string => {
  if (!status) return '';

  switch (status) {
    case AGENT_RINING:
      return AGENT_RINGING;
    case CUSTOMER_RINING:
      return CUSTOMER_RINGING;
    default:
      return status;
  }
};

// Check if agent is offline
export const isOfflinePhase = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [OFFLINE, LOGOUT].includes(normalizedStatus);
};

// Check if agent is online (includes preview, callback)
export const isOnlinePhase = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [ONLINE, PREVIEW, PREVIEW_CAPITALIZE, CALLBACK].includes(
    normalizedStatus
  );
};

// Check if call is connecting/ringing
export const isConnectingPhase = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [
    AGENT_CONNECTING,
    AGENT_RINING,
    AGENT_RINGING,
    CUSTOMER_CONNECTING,
    CUSTOMER_RINING,
    CUSTOMER_RINGING,
  ].includes(normalizedStatus);
};

// Check if call is connected (includes mute/hold/transfer)
export const isConnectedPhase = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [
    AGENT_CONNECTED,
    CUSTOMER_CONNECTED,
    CONNECTED,
    TRANSFER_START,
    TRANSFER_CALL,
    TRANSFER_TALK,
    AGENT_MUTE,
    MUTE,
    AGENT_HOLD,
    HOLD,
  ].includes(normalizedStatus);
};

// Check if call is disconnecting/disconnected
export const isDisconnectedPhase = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [CUSTOMER_DISCONNECTED, CUSTOMER_DISCONNECTING].includes(
    normalizedStatus
  );
};

// Check if agent is in After Call Work
export const isACWPhase = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return normalizedStatus === ACW;
};

// Check if agent is on break
export const isBreakPhase = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [
    BREAK,
    LUNCH_BREAK,
    TEA_BREAK,
    MEETING_BREAK,
    BIOMETRIC_BREAK,
    MOBILE_BREAK,
  ].includes(normalizedStatus);
};

// Check if agent is in any call phase (connecting/connected/disconnected/ACW)
export const isInCallPhase = (status: string): boolean => {
  return (
    isConnectingPhase(status) ||
    isConnectedPhase(status) ||
    isDisconnectedPhase(status) ||
    isACWPhase(status)
  );
};

// Check if agent is available to take new calls
export const isAvailableForCalls = (status: string): boolean => {
  return isOnlinePhase(status) && !isInCallPhase(status);
};

// Legacy functions (deprecated - use phase-based functions instead)

// @deprecated Use isOnlinePhase
export const isOnline = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [ONLINE].includes(normalizedStatus);
};

// @deprecated Use isBreakPhase
export const isBreak = (status: string): boolean => {
  return isBreakPhase(status);
};

// @deprecated Use isOfflinePhase
export const isOffline = (status: string): boolean => {
  return isOfflinePhase(status);
};

// Check if call is in transfer state
export const isTransfer = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [TRANSFER_START, TRANSFER_CALL, TRANSFER_TALK].includes(
    normalizedStatus
  );
};

// Check if call is muted
export const isMute = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [MUTE, AGENT_MUTE].includes(normalizedStatus);
};

// Check if call is on hold
export const isHold = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [HOLD, AGENT_HOLD].includes(normalizedStatus);
};

// @deprecated Use isACWPhase
export const isACW = (status: string): boolean => {
  return isACWPhase(status);
};

// Check if Tubelight iframe should be shown
export const isShowIframe = (status: string): boolean => {
  return (
    isOfflinePhase(status) ||
    isOnlinePhase(status) ||
    isConnectingPhase(status) ||
    isConnectedPhase(status) ||
    isDisconnectedPhase(status) ||
    isACWPhase(status) ||
    isBreakPhase(status)
  );
};

// Check if Tubelight iframe should be hidden
export const isHideIframe = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return normalizedStatus === ONLINE || isACWPhase(status);
};

export interface CallStatus {
  status: string;
  duration?: number | null;
  startTime?: string | null;
  metadata?: Record<string, unknown>;
}

// Get status color for UI
export const getStatusColor = (status: string): string => {
  if (isOnlinePhase(status) || isConnectedPhase(status)) {
    return '#70CD59';
  }
  if (isConnectingPhase(status)) {
    return '#FFA800';
  }
  if (isACWPhase(status)) {
    return '#F04438';
  }
  return '#9AA4B2';
};

// Get status type for UI styling
export const getStatusType = (
  status: string
): 'success' | 'error' | 'caution' => {
  if (isOnlinePhase(status) || isConnectedPhase(status)) {
    return 'success';
  }
  if (isACWPhase(status) || isOfflinePhase(status)) {
    return 'error';
  }
  return 'caution';
};

// Get user-friendly status message and icon
export const getStatusMessage = (
  status: string
): { text: string; icon: string } => {
  const normalizedStatus = normalizeStatus(status);

  if (isOfflinePhase(normalizedStatus)) {
    if (normalizedStatus === LOGOUT) {
      return { text: 'Logout', icon: 'phone' };
    }
    return { text: 'Offline', icon: 'phone' };
  }

  if (isOnlinePhase(normalizedStatus)) {
    if (
      normalizedStatus === PREVIEW ||
      normalizedStatus === PREVIEW_CAPITALIZE
    ) {
      return { text: 'Preview', icon: 'phone' };
    }
    if (normalizedStatus === CALLBACK) {
      return { text: 'Callback', icon: 'phone' };
    }
    return { text: 'Online', icon: 'phone' };
  }

  if (isConnectingPhase(normalizedStatus)) {
    if (
      normalizedStatus === AGENT_CONNECTING ||
      normalizedStatus === AGENT_RINGING
    ) {
      return { text: 'Agent connecting...', icon: 'phone' };
    }
    return { text: 'Customer ringing...', icon: 'phone' };
  }

  if (isConnectedPhase(normalizedStatus)) {
    if (normalizedStatus === AGENT_CONNECTED) {
      return { text: 'Agent connected', icon: 'phone' };
    }
    if (isMute(normalizedStatus)) {
      return { text: 'Mute', icon: 'phone' };
    }
    if (isHold(normalizedStatus)) {
      return { text: 'Hold', icon: 'phone' };
    }
    if (isTransfer(normalizedStatus)) {
      if (normalizedStatus === TRANSFER_START) {
        return { text: 'Transfer start', icon: 'phone' };
      }
      if (normalizedStatus === TRANSFER_CALL) {
        return { text: 'Transfer call', icon: 'phone' };
      }
      return { text: 'Transfer talk', icon: 'phone' };
    }
    return { text: 'Ongoing call...', icon: 'phone' };
  }

  if (isDisconnectedPhase(normalizedStatus)) {
    if (normalizedStatus === CUSTOMER_DISCONNECTING) {
      return { text: 'Customer disconnecting...', icon: 'phone' };
    }
    return { text: 'Customer disconnected', icon: 'phone' };
  }

  if (isACWPhase(normalizedStatus)) {
    return { text: 'Call ended', icon: 'phone' };
  }

  if (isBreakPhase(normalizedStatus)) {
    if (normalizedStatus === LUNCH_BREAK) {
      return { text: 'Lunch break', icon: 'phone' };
    }
    if (normalizedStatus === TEA_BREAK) {
      return { text: 'Tea break', icon: 'phone' };
    }
    if (normalizedStatus === MEETING_BREAK) {
      return { text: 'Meeting break', icon: 'phone' };
    }
    if (normalizedStatus === BIOMETRIC_BREAK) {
      return { text: 'Biometric break', icon: 'phone' };
    }
    if (normalizedStatus === MOBILE_BREAK) {
      return { text: 'Mobile break', icon: 'phone' };
    }
    return { text: 'Break', icon: 'phone' };
  }

  return { text: 'Unknown status', icon: 'phone' };
};

// @deprecated Use isConnectingPhase
export const isConnecting = (status: string): boolean => {
  return isConnectingPhase(status);
};

// @deprecated Use isConnectedPhase
export const isCallConnected = (status: string): boolean => {
  const normalizedStatus = normalizeStatus(status);
  return [CUSTOMER_CONNECTED, AGENT_CONNECTED, CONNECTED].includes(
    normalizedStatus
  );
};

// @deprecated Use isDisconnectedPhase
export const isDisconnected = (status: string): boolean => {
  return isDisconnectedPhase(status);
};

// @deprecated Use isInCallPhase
export const isInActiveCall = (status: string): boolean => {
  return isInCallPhase(status);
};

// @deprecated Use phase-based functions directly
export const getCallPhase = (
  status: string
): 'pre-call' | 'active-call' | 'post-call' | 'not-in-call' => {
  if (isConnectingPhase(status)) {
    return 'pre-call';
  }

  if (isConnectedPhase(status)) {
    return 'active-call';
  }

  if (isDisconnectedPhase(status) || isACWPhase(status)) {
    return 'post-call';
  }

  return 'not-in-call';
};

// Get normalized status for display purposes
export const getNormalizedStatus = (status: string): string => {
  const normalizedStatus = normalizeStatus(status);

  if (isOfflinePhase(normalizedStatus)) {
    return 'offline';
  }

  if (isOnlinePhase(normalizedStatus)) {
    return 'online';
  }

  if (isConnectingPhase(normalizedStatus)) {
    return 'connecting';
  }

  if (isConnectedPhase(normalizedStatus)) {
    return 'connected';
  }

  if (isDisconnectedPhase(normalizedStatus)) {
    return 'disconnected';
  }

  if (isACWPhase(normalizedStatus)) {
    return 'after-call-work';
  }

  if (isBreakPhase(normalizedStatus)) {
    return 'break';
  }

  return normalizedStatus.toLowerCase();
};

// @deprecated Use isAvailableForCalls
export const canTakeNewCalls = (status: string): boolean => {
  return isAvailableForCalls(status);
};

// Check if status duration requires attention (e.g., ACW too long)
export const statusNeedsAttention = (
  status: string,
  duration?: number | null
): boolean => {
  const normalizedStatus = normalizeStatus(status);
  if (!duration) return false;

  const now = Math.floor(Date.now() / 1000);
  const elapsedSeconds = now - duration;

  // ACW shouldn't last more than 5 minutes
  if (isACWPhase(normalizedStatus) && elapsedSeconds > 5 * 60) {
    return true;
  }

  // Break shouldn't last more than 30 minutes (except lunch)
  if (
    isBreakPhase(normalizedStatus) &&
    normalizedStatus !== LUNCH_BREAK &&
    elapsedSeconds > 30 * 60
  ) {
    return true;
  }

  // Lunch shouldn't last more than 60 minutes
  if (normalizedStatus === LUNCH_BREAK && elapsedSeconds > 60 * 60) {
    return true;
  }

  return false;
};
